import {
  SCORED_SLEEP_STUDY_UPLOAD_TYPE,
  getSleepStudyFileCollection,
  isScoredSleepStudyUpload,
} from "../../../utils/sleepStudyFiles";
import { hasTechnicianCheckDoctorCopy } from "./BookingTechnicianReportCheck";

export const UNASSIGNED_NAME = "กรุณาเลือก";
export const SLEEP_TEST_SCHEDULED_STATUS = "Sleep Test Scheduled";
export const TECHNICIAN_SCORED_STATUS = "Technician Scored";
export const ASSIGNED_STATUS = "Assigned";
export const ASSIGNED_TO_DOCTOR_STATUS = "Assigned to Doctor";
export const DOCTOR_SCORED_STATUS = "Doctor Scored";
export const SCORED_UPLOAD_TYPE = SCORED_SLEEP_STUDY_UPLOAD_TYPE;
export const BOOKING_UPLOAD_BOOKING_PATH_FIELD = "bookingUploadBookingPath";
export const REVIEW_REQUIRED_STATUS = "review_required";

const DAY_MS = 1000 * 60 * 60 * 24;

const getFirstExtractFile = (ep = {}) => {
  const files = ep.officeExtractFiles || {};
  const firstKey = Object.keys(files)[0];
  return firstKey ? files[firstKey] || {} : {};
};

export const getWorkflowExtractStatus = (ep = {}) => {
  const primary = getFirstExtractFile(ep);
  return ep.officeExtractStatus || primary.status || "idle";
};

export const isRawCaseReadyForAssignment = (ep = {}) =>
  getWorkflowExtractStatus(ep) === "done";

export const hasRawSleepStudyUpload = (ep = {}) => {
  const uploads = [
    ...(Array.isArray(ep.sleepstudy) ? ep.sleepstudy : []),
    ...(ep.latestSleepstudyWithFile ? [ep.latestSleepstudyWithFile] : []),
  ];
  return uploads.some((upload) => upload && !isScoredSleepStudyUpload(upload) &&
    Array.isArray(upload.reportFile) && upload.reportFile.length > 0);
};

export const getScoredCaseSourceChoices = (ep = {}) =>
  Object.entries(ep.officeScoreExtractFiles || {}).flatMap(([id, source]) => {
    if (!source || !["done", REVIEW_REQUIRED_STATUS].includes(source.status)) return [];
    const path = source.extractPath || "";
    const relative = path.replace(/^\/srv\/sleep_center\/tech_done_from_client\//, "");
    const folder = relative.split("/")[0];
    if (
      relative === path || relative.split("/").some((part) => part === "..") ||
      !ep.order || !(folder === ep.order || folder.startsWith(`${ep.order}_`))
    ) return [];
    const sourceType = source.sourceType || "outside_upload";
    const label = sourceType === "center_client"
      ? `Center client return${source.technician ? ` (${source.technician})` : ""}`
      : sourceType === "merged" ? "Merged case" : "Outside scored upload";
    return [{ id, path, sourceType, label,
      fileName: source.zipFileName || source.extractFolderName || folder }];
  }).filter((source, index, sources) =>
    sources.findIndex((other) => other.path === source.path) === index);

const getScoredUploadPaths = (ep = {}, bookingPath = "") => {
  return (Array.isArray(ep.sleepstudy) ? ep.sleepstudy : []).flatMap((upload) => {
    if (!upload || !isScoredSleepStudyUpload(upload)) return [];
    if (bookingPath && upload.bookingUploadBookingPath &&
        upload.bookingUploadBookingPath !== bookingPath) return [];
    const files = Array.isArray(upload.reportFile) ? upload.reportFile : [];
    return files.filter((name) => {
      if (typeof name !== "string" || !name.toLowerCase().endsWith(".zip")) return false;
      const namedOrder = name.match(/^([A-Za-z0-9]+(?:-[A-Za-z0-9]+)*-SS-[0-9]+)(?:$|[_ .(])/i);
      return !namedOrder || !ep.order || namedOrder[1].toLowerCase() === ep.order.toLowerCase();
    }).map((name) => `${getSleepStudyFileCollection(upload)}s/${upload.hn || ep.hn}/${upload.id}/${name}`);
  });
};

export const getPendingScoredUploadPaths = (ep = {}, bookingPath = "") => {
  const extractedPaths = new Set(Object.values(ep.officeScoreExtractFiles || {})
    .filter((source) => source && ["done", "consumed", REVIEW_REQUIRED_STATUS].includes(source.status))
    .map((source) => source.storagePath).filter(Boolean));
  const consumed = (ep.technicianReportCheck || {}).consumedStoragePaths;
  (Array.isArray(consumed) ? consumed : []).forEach((path) => extractedPaths.add(path));
  if (ep.officeScoreExtractStatus === "done") {
    (Array.isArray(ep.officeScoreStoragePath) ? ep.officeScoreStoragePath : [])
      .forEach((path) => extractedPaths.add(path));
  }
  return getScoredUploadPaths(ep, bookingPath).filter((path) => !extractedPaths.has(path));
};

export const isScoredCaseReviewRequired = (ep = {}) => {
  if (ep.officeScoreExtractStatus === REVIEW_REQUIRED_STATUS) return true;
  const sources = getScoredCaseSourceChoices(ep);
  const latestReview = (Array.isArray(ep.monitorManualOverrideLog) ? [...ep.monitorManualOverrideLog] : [])
    .reverse().find((entry) => entry && entry.multiFileReviewConfirmed && entry.toStatus === ASSIGNED_TO_DOCTOR_STATUS);
  const sameSources = !latestReview || !Array.isArray(latestReview.scoredSourceIds) || (
    latestReview.scoredSourceIds.length === sources.length &&
    sources.every((source) => latestReview.scoredSourceIds.includes(source.id))
  );
  return sources.length > 1 && !(
    ep.officeScoreMultiFileReviewStatus === "reviewed" &&
    ep.officeScoreExtractStatus === "done" &&
    sameSources &&
    sources.some((source) => source.path === ep.officeScoreExtractPath)
  );
};

export const buildScoredCaseReviewProps = (ep, selectedPath, reviewedBy, reviewedAt, bookingPath = "") => {
  if (getPendingScoredUploadPaths(ep, bookingPath).length) {
    throw new Error("Wait until the center has downloaded and extracted every scored upload.");
  }
  if (!getScoredCaseSourceChoices(ep).some((source) => source.path === selectedPath)) {
    throw new Error("Select the approved scored version for the doctor.");
  }
  return {
    officeScoreExtractStatus: "done",
    officeScoreExtractError: "",
    officeScoreExtractPath: selectedPath,
    officeScoreMultiFileReviewStatus: "reviewed",
    officeScoreMultiFileReviewedAt: reviewedAt,
    officeScoreMultiFileReviewedBy: reviewedBy,
    ...(!["copied_to_client", "returned_to_center"].includes(ep.doctorTransferStatus)
      ? { doctorTransferStatus: "ready", doctorTransferError: "" } : {}),
  };
};

export const buildScoredUploadWaitProps = (ep = {}) => ({
  officeScoreDownloadStatus: "waiting_upload",
  officeScoreDownloadError: "",
  officeScoreExtractStatus: "waiting_upload",
  officeScoreExtractError: "Waiting for center scored upload download/extract.",
  officeScoreMultiFileReviewStatus: "",
  ...(!["copied_to_client", "returned_to_center"].includes(ep.doctorTransferStatus)
    ? { doctorTransferStatus: "waiting_scored_files",
        doctorTransferError: "Waiting for center scored upload download/extract." } : {}),
});

export const buildReturnedScoredCaseProps = (ep = {}, bookingPath = "") => {
  // Older Path A returns can retain the outside-upload wait written before
  // the client returned. Only clear it after the upload inventory is loaded.
  if (
    ep.techTransferStatus !== "returned_to_center" ||
    ep.officeScoreExtractStatus !== "waiting_upload" ||
    !["", "idle", "waiting_upload"].includes(ep.officeScoreDownloadStatus || "") ||
    !Array.isArray(ep.sleepstudy) ||
    getScoredUploadPaths(ep, bookingPath).length ||
    (ep.officeScoreStoragePath || []).length ||
    Object.keys(ep.officeScoreExtractFiles || {}).length ||
    ep.officeScoreMultiFileReviewStatus === REVIEW_REQUIRED_STATUS ||
    ep.doctorTransferStatus === REVIEW_REQUIRED_STATUS
  ) return {};

  // Restore the legacy client-return state, not a completed ZIP extraction.
  // Center/client automation still verifies the returned folder before copying.
  return {
    officeScoreExtractStatus: "",
    officeScoreExtractError: "",
    ...(ep.officeScoreDownloadStatus === "waiting_upload"
      ? { officeScoreDownloadStatus: "", officeScoreDownloadError: "" } : {}),
  };
};

export const isScoredCaseReadyForDoctor = (ep = {}, bookingPath = "") => {
  if (isScoredCaseReviewRequired(ep) || getPendingScoredUploadPaths(ep, bookingPath).length) return false;
  if (Object.keys(buildReturnedScoredCaseProps(ep, bookingPath)).length) return true;
  if (ep.officeScoreExtractStatus && ep.officeScoreExtractStatus !== "done") return false;
  return (
    ep.techTransferStatus === "returned_to_center" ||
    ep.officeScoreExtractStatus === "done"
  );
};

export const getScoredCaseStatus = (ep = {}, bookingPath = "") => {
  if (isScoredCaseReviewRequired(ep)) return REVIEW_REQUIRED_STATUS;
  if (Object.keys(buildReturnedScoredCaseProps(ep, bookingPath)).length) return "done";
  const download = ep.officeScoreDownloadStatus || "";
  const extract = ep.officeScoreExtractStatus || "";
  const failures = ["failed", "error", "partial_error", "disk_space_error", "center_disk_space_error"];
  if (failures.includes(download)) return download;
  if (failures.includes(extract)) return extract;
  if (download === "downloading") return "downloading";
  if (["extracting", "waiting_extract"].includes(extract)) return extract;
  if (getPendingScoredUploadPaths(ep, bookingPath).length) return "waiting_download";
  if (isScoredCaseReadyForDoctor(ep, bookingPath)) return "done";
  // A confirmed client return has already supplied scored files. Any remaining
  // verification is a center-file wait, not a request for an outside ZIP upload.
  const uploadWait = ep.techTransferStatus === "returned_to_center"
    ? "waiting_scored_files" : "waiting_upload";
  if (extract === "waiting_upload") return uploadWait;
  if (extract && extract !== "done") return extract;
  if (download === "waiting_upload") return uploadWait;
  if ([TECHNICIAN_SCORED_STATUS, ASSIGNED_TO_DOCTOR_STATUS].includes(ep.status)) {
    return "waiting_scored_files";
  }
  return "idle";
};

export const hasCenterRawWorkflow = (ep = {}) =>
  Boolean(
    ep.officeDownloadStatus ||
      ep.officeExtractStatus ||
      ep.techTransferStatus,
  );

export const isDirectTechnicianScoredUpload = (ep = {}) =>
  ep.status === TECHNICIAN_SCORED_STATUS && !hasCenterRawWorkflow(ep);

export const buildSleepstudyUploadMetadata = (
  ep = {},
  currentUser = null,
  bookingPath = "",
  scoreUploadSource = "booking_monitor",
  uploadType = "",
) => {
  const metadata = {
    createBy:
      (currentUser && (currentUser.displayName || currentUser.email)) || "",
    createAt: new Date(),
  };

  if (bookingPath) {
    metadata[BOOKING_UPLOAD_BOOKING_PATH_FIELD] = bookingPath;
  }

  if (uploadType) metadata.scoreUploadType = uploadType;

  if (
    uploadType === SCORED_UPLOAD_TYPE ||
    (!uploadType && isDirectTechnicianScoredUpload(ep))
  ) {
    metadata.scoreUploadType = SCORED_UPLOAD_TYPE;
    metadata.scoreUploadSource = scoreUploadSource;
  }

  return metadata;
};

export const isDirectTechnicianScoredTransition = ({
  currentExtendedProps = {},
  nextStatus = "",
} = {}) => {
  return (
    nextStatus === TECHNICIAN_SCORED_STATUS &&
    currentExtendedProps.status === SLEEP_TEST_SCHEDULED_STATUS &&
    !hasCenterRawWorkflow(currentExtendedProps)
  );
};

export const isUnmarkedSleepstudyZipUpload = (upload = {}) => {
  const reportFiles = upload.reportFile;
  const firstFile = Array.isArray(reportFiles) ? reportFiles[0] : "";

  return (
    !upload.scoreUploadType &&
    typeof firstFile === "string" &&
    firstFile.toLowerCase().endsWith(".zip")
  );
};

const parseDateValue = (value) => {
  if (!value) return null;
  if (value && typeof value.toDate === "function") return value.toDate();
  if (
    value &&
    typeof value === "object" &&
    typeof value.seconds === "number"
  ) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const roundAwayFromZero = (value) =>
  value < 0 ? Math.floor(value) : Math.ceil(value);

const getDateTime = (value) => {
  const date = parseDateValue(value);
  return date ? date.getTime() : 0;
};

const getCaseExtendedProps = (item = {}) => item.extendedProps || {};

const getCaseSleepStudyDateTime = (item = {}) => {
  const ep = getCaseExtendedProps(item);
  return getDateTime(ep.latestSleepStudyDate || item.start || ep.start);
};

const getCaseStartTime = (item = {}) => getDateTime(item.start);

const getAssignedTech = (ep = {}) => ep.technician || UNASSIGNED_NAME;

const getAssignedDoctor = (ep = {}) => ep.doctor || UNASSIGNED_NAME;

const formatDateOnly = (value) => {
  const date = parseDateValue(value);
  if (!date) return "-";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getSleepStudyMonitorDisplayDate = (item = {}) => {
  const ep = getCaseExtendedProps(item);
  return formatDateOnly(ep.latestSleepStudyDate || item.start || ep.start);
};

export const getSleepStudyDueDays = (ep = {}, nodeInfo = []) => {
  if (ep.healthInsurance === "ชำระเงินเอง") return 7;

  const matchedNode = nodeInfo.find((node) => node.nodename === ep.node);
  return (matchedNode && matchedNode.dueDays) || 30;
};

export const getSleepStudyDeadlineInfo = (
  ep = {},
  nodeInfo = [],
  today = new Date(),
) => {
  if (ep.status === DOCTOR_SCORED_STATUS) return null;

  const sleepStudyDate = parseDateValue(ep.latestSleepStudyDate || ep.start);
  if (!sleepStudyDate) return null;

  const todayDate = parseDateValue(today) || new Date();
  if (sleepStudyDate > todayDate) return null;

  const dueDays = getSleepStudyDueDays(ep, nodeInfo);
  const daysDiff = (todayDate - sleepStudyDate) / DAY_MS;
  const daysLeft = dueDays - daysDiff;
  const daysLeftRounded = roundAwayFromZero(daysLeft);
  const absDays = Math.abs(daysLeftRounded);

  let level = "normal";
  if (ep.healthInsurance === "ชำระเงินเอง") {
    if (daysDiff > dueDays + 1) level = "danger";
    else if (daysDiff > dueDays - 1 && daysDiff < 8) level = "warning";
  } else if (daysDiff > dueDays) {
    level = "danger";
  } else if (daysDiff > 0.7 * dueDays && daysDiff < dueDays) {
    level = "warning";
  }

  let text = "Deadline: Due today";
  if (daysLeftRounded > 0) {
    text = `Deadline: ${daysLeftRounded} ${
      daysLeftRounded === 1 ? "day" : "days"
    } left`;
  } else if (daysLeftRounded < 0) {
    text = `Deadline: ${absDays} ${absDays === 1 ? "day" : "days"} overdue`;
  }

  return {
    dueDays,
    daysDiff,
    daysLeft,
    daysLeftRounded,
    level,
    text,
  };
};

export const compareSleepStudyMonitorCases = (
  a,
  b,
  { sortBy = "deadline", nodeInfo = [], today = new Date() } = {},
) => {
  const epA = getCaseExtendedProps(a);
  const epB = getCaseExtendedProps(b);
  const selfPayA = epA.healthInsurance === "ชำระเงินเอง" ? 1 : 0;
  const selfPayB = epB.healthInsurance === "ชำระเงินเอง" ? 1 : 0;

  if (sortBy === "deadline") {
    if (selfPayA !== selfPayB) return selfPayB - selfPayA;

    const deadlineA = getSleepStudyDeadlineInfo(
      { ...epA, start: a && a.start },
      nodeInfo,
      today,
    );
    const deadlineB = getSleepStudyDeadlineInfo(
      { ...epB, start: b && b.start },
      nodeInfo,
      today,
    );
    const daysA = deadlineA ? deadlineA.daysLeft : Number.POSITIVE_INFINITY;
    const daysB = deadlineB ? deadlineB.daysLeft : Number.POSITIVE_INFINITY;
    if (daysA !== daysB) return daysA - daysB;

    return getCaseStartTime(a) - getCaseStartTime(b);
  }

  if (sortBy === "newest") {
    return getCaseSleepStudyDateTime(b) - getCaseSleepStudyDateTime(a);
  }

  if (sortBy === "oldest") {
    return getCaseSleepStudyDateTime(a) - getCaseSleepStudyDateTime(b);
  }

  if (selfPayA !== selfPayB) return selfPayB - selfPayA;

  const techA = getAssignedTech(epA);
  const techB = getAssignedTech(epB);
  const doctorA = getAssignedDoctor(epA);
  const doctorB = getAssignedDoctor(epB);

  if (techA === UNASSIGNED_NAME && techB !== UNASSIGNED_NAME) return -1;
  if (techB === UNASSIGNED_NAME && techA !== UNASSIGNED_NAME) return 1;

  if (doctorA === UNASSIGNED_NAME && doctorB !== UNASSIGNED_NAME) return -1;
  if (doctorB === UNASSIGNED_NAME && doctorA !== UNASSIGNED_NAME) return 1;

  return getCaseStartTime(a) - getCaseStartTime(b);
};

const countReportFiles = (rows = []) =>
  rows.reduce(
    (sum, row) =>
      sum + (Array.isArray(row && row.reportFile) ? row.reportFile.length : 0),
    0,
  );

export const getMonitorFileCounts = (ep = {}) => {
  const reportCount = countReportFiles(ep.reports || []);
  const sleepStudyCount = countReportFiles(ep.sleepstudy || []);

  return {
    reportCount,
    sleepStudyCount,
    total: reportCount + sleepStudyCount,
  };
};

export const getMonitorIssueVariant = (errorType = "") => {
  if (errorType === "Extract" || errorType === "Scored Extract") return "warning";
  if (
    errorType === "Download" ||
    errorType === "Scored Download" ||
    errorType === "Disk Space" ||
    errorType === "Client Disk Space" ||
    errorType === "Center Disk Space" ||
    errorType.includes("Transfer")
  ) {
    return "danger";
  }

  return "secondary";
};

export const getMonitorSaveBlockReason = ({
  currentExtendedProps = {},
  nextStatus = "",
  nextTechnician = UNASSIGNED_NAME,
  nextDoctor = UNASSIGNED_NAME,
  bookingPath = "",
}) => {
  const currentStatus = currentExtendedProps.status || "";
  const currentTechnician =
    currentExtendedProps.technician || UNASSIGNED_NAME;
  const currentDoctor = currentExtendedProps.doctor || UNASSIGNED_NAME;

  if (
    currentExtendedProps.techTransferStatus === "copied_to_client" &&
    currentTechnician !== nextTechnician
  ) {
    return "Technician already copied this case. Reassign is unsafe.";
  }

  if (
    (currentExtendedProps.doctorTransferStatus === "copied_to_client" ||
      hasTechnicianCheckDoctorCopy(currentExtendedProps)) &&
    currentDoctor !== nextDoctor
  ) {
    return "Doctor already copied this case. Reassign is unsafe.";
  }

  if (nextStatus === ASSIGNED_STATUS && currentStatus !== ASSIGNED_STATUS) {
    if (nextTechnician === UNASSIGNED_NAME) {
      return "Please select Tech Score before changing to Assigned.";
    }
    if (
      getWorkflowExtractStatus(currentExtendedProps) === REVIEW_REQUIRED_STATUS
    ) {
      return "Multiple sleep study files are preserved separately. Admin must review/merge them before changing to Assigned.";
    }
    if (!isRawCaseReadyForAssignment(currentExtendedProps)) {
      return "Raw sleep study extract is not done. Please wait until extract is OK before changing to Assigned.";
    }
  }

  if (
    nextStatus === ASSIGNED_TO_DOCTOR_STATUS &&
    (currentStatus !== ASSIGNED_TO_DOCTOR_STATUS || isScoredCaseReviewRequired(currentExtendedProps))
  ) {
    if (nextDoctor === UNASSIGNED_NAME) {
      return "Please select Doctor before changing to Assigned to Doctor.";
    }
    if (getPendingScoredUploadPaths(currentExtendedProps, bookingPath).length) {
      return "Scored files are waiting for center download/extract. Please wait before assigning a doctor.";
    }
    if (isScoredCaseReviewRequired(currentExtendedProps)) {
      return "Multiple scored files are preserved separately. Admin must review/merge them before changing to Assigned to Doctor.";
    }
    if (!isScoredCaseReadyForDoctor(currentExtendedProps, bookingPath)) {
      return "Scored files are not ready for doctor review. Please recheck tech return or scored upload extract first.";
    }
  }

  return "";
};
